import { useEffect, useCallback, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import cx from 'classnames';

import { usePingMutation } from '@greenkub-fronts/data-access';
import { PageLogin } from '@greenkub-fronts/pages/login';
import { ROUTER } from './router/main.router';

import {
  BannerStaging,
  Snackbar,
  ProtectedRoute,
  LoaderCube,
  Modal,
  Button,
  Icon,
  IconEnum,
} from '@greenkub-fronts/shared/ui';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  RootState,
  UiTheme
} from '@greenkub-fronts/shared/interface';

import { usePostHog } from 'posthog-js/react';
import TagManager from 'react-gtm-module';
import { isArray } from 'lodash';
import { loginWithToken } from '@greenkub-fronts/domain/auth';

export function App() {
  const location = useLocation();

  const [ping] = usePingMutation();
  const postHog = usePostHog();

  const dispatch = useDispatch<AppDispatch>();

  const { sideBarIsOpen, currentTheme } = useSelector(
    (state: RootState) => state.ui
  );
  const { user, role } = useSelector((state: RootState) => state.auth);

  const pingHandler = useCallback(() => {
    if (user) {
      ping();
    }
  }, [user]);

  // Ping Activity
  useEffect(() => {
    pingHandler();
    let interval: any = null;
    interval = setInterval(ping, 1000 * 60 * 5); // toutes les 5mn
    return () => {
      clearInterval(interval);
    };
  }, []);

  // PostHog analytics
  useEffect(() => {
    if (user) {
      postHog?.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: role,
      });
    } else {
      postHog?.reset();
    }
  }, [postHog, role, user]);

  // GTM analytics
  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'page_view',
        pagePath: location.pathname,
        userId: user?.id,
      },
    });
  }, [location.pathname, user?.id]);

  // redirect when connection is done by token
  useEffect(() => {
    const split = location.search.split('&');
    if (isArray(split) && split.length === 1 && split[0].includes('token')) {
      const token = split[0].replace('?token=', '');
      dispatch(loginWithToken({ token }));
    }
  }, [dispatch, location.search]);

  const logoutAndRedirectToCustomerArea = () => {
    localStorage.removeItem('auth');
    window.location.replace(import.meta.env.VITE_CRM_URL + '');
  };

  /**
   * REDIRECTION SI CONNECTE EN TEMPS QUE CLIENT
   */
  if (role === 'GK') {
    return (
      <Modal className={'RedirectModal'} isOpened={true}>
        <div>
          <Icon name={IconEnum.LOGO} className="mb50" />

          <h1>Ceci est l'espace client</h1>

          <p>
            Et vous tentez de vous connecter avec un compte employé. C'est
            impossible
          </p>

          <p>
            <strong>
              L'adresse à utiliser est : {import.meta.env.VITE_CRM_URL}
            </strong>
          </p>

          <Button onClick={() => logoutAndRedirectToCustomerArea()}>
            <span>
              Je pars m'authentifier sur le CRM{' '}
              <Icon name={IconEnum.ARROW_FORWARD_IOS_ROUNDED} />
            </span>
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Suspense fallback={<LoaderCube isLoading={true} />}>
      <BannerStaging />

      <Snackbar />

      <Routes location={location}>
        <Route path={'login/*'} element={<PageLogin />} />

        {ROUTER.map(
          (route) =>
            !route.protected && (
              <Route
                key={route.path}
                path={route.path}
                element={route.component}
              />
            )
        )}

        {ROUTER.map(
          (route) =>
            route.protected && (
              <Route
                key={route.path}
                path={route.path}
                element={
                  !route.layout ? (
                    <ProtectedRoute>{route.component}</ProtectedRoute>
                  ) : (
                    <div
                      className={cx(
                        'Home',
                        sideBarIsOpen && currentTheme !== UiTheme.GEKA
                          ? 'sidebarIsOpen'
                          : 'sidebarIsClosed'
                      )}
                    >
                      <ProtectedRoute>{route.component}</ProtectedRoute>
                    </div>
                  )
                }
              />
            )
        )}
      </Routes>
    </Suspense>
  );
}

export default App;
